import { proxy, subscribe } from "valtio";
import storage from "../utils/storage";
import {
  getAudioCapture,
  destroyAudioCapture,
} from "../utils/voice-agent/audioCapture";

// --- Types (Preserved from original) ---

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "stopped"
  | "error";

export type Speaker = "idle" | "user" | "ai";

export type InterviewState =
  | "idle"
  | "user-speaking"
  | "ai-thinking"
  | "ai-speaking";

export type TranscriptItem = {
  text: string;
  role: "user" | "assistant";
  itemId: string;
};

interface VoiceAgentState {
  // Connection & Session State
  status: ConnectionStatus;
  speaker: Speaker;
  isMuted: boolean;
  userVolume: number; // 0-1 for animations
  isBuffering: boolean;
  transcript: TranscriptItem | null;
  showTranscript: boolean;

  // Microphone permission state
  hasMicPermission: boolean | null;

  // Error message for connection issues
  errorMessage: string | null;

  // Legacy state
  state: InterviewState;
}

// --- Storage Keys ---
const SHOW_TRANSCRIPT_KEY = "voiceAgent_showTranscript";

// --- Initial State ---
// We read preferences from storage, but keep session status ephemeral
const initialShowTranscript = storage.get(SHOW_TRANSCRIPT_KEY) ?? false;

export const voiceAgentStore = proxy<VoiceAgentState>({
  status: "idle",
  speaker: "idle",
  isMuted: false,
  isBuffering: false,
  transcript: null,
  showTranscript: initialShowTranscript,
  userVolume: 0,
  state: "idle",
  hasMicPermission: null,
  errorMessage: null,
});

// --- Persistence Subscription ---
// Automatically save specific preferences when they change
subscribe(voiceAgentStore, () => {
  storage.set(SHOW_TRANSCRIPT_KEY, voiceAgentStore.showTranscript);
});

// --- Actions ---

export const voiceAgentActions = {
  setStatus(status: ConnectionStatus) {
    voiceAgentStore.status = status;
  },

  setSpeaker(speaker: Speaker) {
    voiceAgentStore.speaker = speaker;
  },

  setMuted(isMuted: boolean) {
    voiceAgentStore.isMuted = isMuted;
  },

  toggleMute() {
    if (voiceAgentStore.isMuted) {
      // Instant unmute
      voiceAgentStore.isMuted = false;
    } else {
      // Delayed mute (1 second)
      console.log("⏳ Muting in 1 second...");
      setTimeout(() => {
        voiceAgentStore.isMuted = true;
        console.log("🔇 Muted");
      }, 1000);
    }
  },

  setIsBuffering(isBuffering: boolean) {
    voiceAgentStore.isBuffering = isBuffering;
  },

  setTranscript(transcript: TranscriptItem | null) {
    voiceAgentStore.transcript = transcript;
  },

  setShowTranscript(show: boolean) {
    voiceAgentStore.showTranscript = show;
  },

  toggleShowTranscript() {
    voiceAgentStore.showTranscript = !voiceAgentStore.showTranscript;
  },

  setUserVolume(volume: number) {
    voiceAgentStore.userVolume = volume;
  },

  setState(state: InterviewState) {
    voiceAgentStore.state = state;
  },

  setHasMicPermission(hasPermission: boolean | null) {
    voiceAgentStore.hasMicPermission = hasPermission;
  },

  reset() {
    destroyAudioCapture();

    voiceAgentStore.status = "idle";
    voiceAgentStore.speaker = "idle";
    voiceAgentStore.isMuted = false;
    voiceAgentStore.isBuffering = false;
    voiceAgentStore.userVolume = 0;
    voiceAgentStore.state = "idle";
    voiceAgentStore.hasMicPermission = null;
    // We do NOT reset showTranscript as that is a user preference
  },

  async startSession() {
    console.log("🚀 Starting session...");

    voiceAgentStore.status = "connecting";
    voiceAgentStore.speaker = "idle";
    voiceAgentStore.isMuted = false;
    voiceAgentStore.isBuffering = false;

    try {
      // Get audio capture instance
      const audioCapture = getAudioCapture({
        chunkSizeBytes: 4096, // Small chunks: 4 KB (~2048 samples at 16kHz)
        processorBufferSize: 256, // Smaller audio processing frame size
        sampleRate: 16000, // 16kHz for voice
        channelCount: 1, // Mono
        vadThreshold: 0.01, // Voice detection threshold
        logChunks: true, // Enable console logging
      });

      // Request microphone permission and start capture
      const permissionGranted = await audioCapture.startCapture();

      if (!permissionGranted) {
        console.error("❌ Microphone permission denied - ending session");

        voiceAgentStore.status = "error";
        voiceAgentStore.hasMicPermission = false;
        voiceAgentStore.speaker = "idle";

        // Wait a moment then reset to idle
        setTimeout(() => {
          voiceAgentStore.status = "idle";
        }, 2000);

        return;
      }

      // Permission granted - continue with session
      console.log("✅ Session started with microphone active");

      voiceAgentStore.status = "connected";
      voiceAgentStore.state = "user-speaking";
      voiceAgentStore.hasMicPermission = true;

      // @TODO: Initialize WebSocket connection here
    } catch (error) {
      console.error("❌ Error starting session:", error);

      voiceAgentStore.status = "error";
      voiceAgentStore.speaker = "idle";

      setTimeout(() => {
        voiceAgentStore.status = "idle";
      }, 2000);
    }
  },

  stopSession() {
    console.log("🛑 Stopping session...");

    // Stop audio capture
    destroyAudioCapture();

    voiceAgentStore.status = "stopped";
    voiceAgentStore.speaker = "idle";
    voiceAgentStore.isMuted = false;
    voiceAgentStore.isBuffering = false;
    voiceAgentStore.userVolume = 0;
    voiceAgentStore.state = "idle";

    // TODO: Close WebSocket connection

    setTimeout(() => {
      voiceAgentStore.status = "idle";
    }, 300);
  },
};
